import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ArrowRight, AlertTriangle, DollarSign, Lock, ShieldCheck, Zap, X, Mail, MessageCircle, Quote, Star } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Section } from '../components/Section';
declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: (...args: unknown[]) => void;
  }
}
function initFacebookPixel(pixelId: string) {
  if (typeof window === 'undefined' || window.fbq) return;
  const n: any = window.fbq = function (...args: unknown[]) {
    n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
  };
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [] as unknown[][];
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);
  window.fbq('init', pixelId);
}
function trackEvent(eventName: string, params?: Record<string, unknown>) {
  if (typeof window !== 'undefined' && window.fbq) {
    if (params) {
      window.fbq('track', eventName, params);
    } else {
      window.fbq('track', eventName);
    }
  }
}
export function LandingPage() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | 'contact' | null>(null);
  useEffect(() => {
    // Defer pixel loading so it doesn't block initial paint
    const loadPixel = () => {
      initFacebookPixel('1617068992902241');
      trackEvent('PageView');
    };
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(loadPixel);
    } else {
      setTimeout(loadPixel, 1500);
    }
    // Preconnect to Shopify checkout domain for faster redirect
    const dnsPrefetch = document.createElement('link');
    dnsPrefetch.rel = 'dns-prefetch';
    dnsPrefetch.href = 'https://fondoinicial.online';
    document.head.appendChild(dnsPrefetch);
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = 'https://fondoinicial.online';
    preconnect.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect);
    // Prefetch the cart page in the background
    const prefetch = document.createElement('link');
    prefetch.rel = 'prefetch';
    prefetch.href = 'https://fondoinicial.online/cart/47689212428527:1';
    document.head.appendChild(prefetch);
  }, []);
  const scrollToOffer = () => {
    document.getElementById('offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-[#07080f] text-gray-300 font-sans selection:bg-blue-500/30 selection:text-blue-200 overflow-x-hidden" data-id="element-7">
      {/* Background Effects */}
      <div className="fixed inset-0 z-0 pointer-events-none" style={{
      willChange: 'transform',
      transform: 'translateZ(0)'
    }} data-id="element-8">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-[800px] h-[400px] bg-blue-900/10 blur-[80px] rounded-full opacity-40" data-id="element-9" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-blue-800/5 blur-[80px] rounded-full" data-id="element-10" />
      </div>

      {/* Hero Section */}
      <header className="relative z-10 pt-20 pb-16 px-4 md:pt-32 md:pb-24" data-id="element-11">
        <div className="max-w-3xl mx-auto text-center" data-id="element-12">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-13">
            <span className="inline-block px-3 py-1 mb-6 text-xs font-bold tracking-widest text-blue-400 uppercase bg-blue-900/20 rounded-full border border-blue-800/50" data-id="element-14">
              Para Argentinos 18-24 Años
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white tracking-tight leading-[1.1] mb-6" data-id="element-15">
              NO NECESITÁS <br className="hidden md:block" data-id="element-16" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600" data-id="element-17">
                MÁS IDEAS.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-400 font-medium max-w-2xl mx-auto mb-8" data-id="element-18">
              Necesitás tu primera venta. <br data-id="element-19" />
              <span className="text-white" data-id="element-20">
                De 0 a tu primer ingreso online (sin humo).
              </span>
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.2,
          duration: 0.5
        }} className="relative mx-auto max-w-sm md:max-w-md mb-10 group" data-id="element-21">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-blue-400 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000" data-id="element-22"></div>
            <img src="https://cdn.magicpatterns.com/uploads/qAoL3v1a41tnG5smGKxBLG/PORTADA_21_2.png" alt="Plan Primer Dólar Portada" className="relative rounded-xl shadow-2xl border border-zinc-800 w-full" decoding="async" fetchPriority="high" width={448} height={448} data-id="element-23" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} data-id="element-24">
            <Button size="lg" onClick={scrollToOffer} className="w-full md:w-auto text-lg shadow-blue-900/20" data-id="element-25">
              QUIERO MI PRIMERA VENTA
              <ArrowRight className="ml-2 h-5 w-5" data-id="element-26" />
            </Button>
            <p className="mt-4 text-sm text-zinc-500" data-id="element-27">
              Sin suscripciones. Pago único. Acceso inmediato.
            </p>
          </motion.div>
        </div>
      </header>

      {/* Problem / Agitation */}
      <Section className="bg-zinc-900/30 border-y border-zinc-800/50" data-id="element-28">
        <div className="prose prose-invert prose-lg mx-auto" data-id="element-29">
          <h2 className="text-3xl font-bold text-white mb-6" data-id="element-30">
            No estás "mal con la plata".
          </h2>
          <p className="text-gray-300" data-id="element-31">
            Estás atrapado en un juego donde cobrás en pesos, todo sube, y
            encima te dicen que la solución es "invertir".
          </p>
          <p className="text-gray-300 font-medium border-l-4 border-blue-600 pl-4 italic my-6" data-id="element-32">
            Pero cuando no te sobra, invertir se siente como apostar.
          </p>
          <p className="text-gray-300" data-id="element-33">
            Y mientras estás mirando videos de "cómo hacer plata", el mes te
            pasa por arriba.
          </p>
          <p className="text-white font-bold text-xl mt-8" data-id="element-34">
            Este PDF no te vende motivación.
          </p>
          <p className="text-gray-300" data-id="element-35">
            Te da una salida chica, concreta y repetible: hacer tu primera venta
            con un micro‑servicio que se pueda entregar rápido, aunque hoy
            sientas que "no tenés nada especial".
          </p>
        </div>
      </Section>

      {/* Identification */}
      <Section data-id="element-36">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12" data-id="element-37">
          Si tenés 18 a 24 y vivís en Argentina...
        </h2>
        <div className="grid gap-4 md:grid-cols-2" data-id="element-38">
          {['Cobrás… y a los pocos días ya estás contando monedas.', 'Querés generar ingresos online, pero no sabés qué vender.', "Te da vergüenza mandar mensajes porque pensás 'me van a ignorar'.", 'Probaste ver videos, guardar tips, bajar apps… y nada cambió.'].map((item, i) => <Card key={i} className="flex items-start gap-4" data-id="element-39">
              <div className="bg-red-500/10 p-2 rounded-lg shrink-0" data-id="element-40">
                <AlertTriangle className="h-6 w-6 text-red-500" data-id="element-41" />
              </div>
              <p className="text-gray-300 font-medium" data-id="element-42">{item}</p>
            </Card>)}
        </div>
        <div className="mt-12 text-center max-w-2xl mx-auto" data-id="element-43">
          <p className="text-xl text-gray-300" data-id="element-44">
            Y lo peor: empezás a sentir que el problema sos vos. Como si te
            faltara disciplina o inteligencia.
          </p>
          <p className="text-2xl font-bold text-white mt-6" data-id="element-45">
            NO. Te falta un sistema simple.
          </p>
          <div className="flex items-center justify-center gap-2 mt-4 text-blue-400 font-mono text-sm md:text-base" data-id="element-46">
            <span data-id="element-47">"no sé qué hacer"</span>
            <ArrowRight className="h-4 w-4" data-id="element-48" />
            <span data-id="element-49">"tengo oferta"</span>
            <ArrowRight className="h-4 w-4" data-id="element-50" />
            <span data-id="element-51">"tengo cliente"</span>
            <ArrowRight className="h-4 w-4" data-id="element-52" />
            <span className="font-bold text-green-400" data-id="element-53">"COBRÉ"</span>
          </div>
        </div>
      </Section>

      {/* The Solution */}
      <Section className="bg-gradient-to-b from-blue-900/10 to-transparent border-t border-blue-900/30" data-id="element-54">
        <div className="text-center mb-12" data-id="element-55">
          <span className="text-blue-500 font-bold tracking-wider uppercase text-sm" data-id="element-56">
            Presentando
          </span>
          <h2 className="text-4xl md:text-5xl font-black text-white mt-2 mb-4" data-id="element-57">
            PLAN PRIMER DÓLAR
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-58">(21 DÍAS)</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6" data-id="element-59">
          <Card glow className="md:col-span-3 bg-blue-900/10 border-blue-800/30" data-id="element-60">
            <h3 className="text-xl font-bold text-white mb-2" data-id="element-61">¿Qué es?</h3>
            <p className="text-gray-300" data-id="element-62">
              Un PDF práctico (con plantillas) para que construyas tu primera
              oferta vendible, armes prueba sin mentir y consigas tu primer
              cliente con guiones listos. No es un "curso para volverte
              millonario". Es un plan para salir del pantano.
            </p>
          </Card>
        </div>

        <div className="mt-16" data-id="element-63">
          <h3 className="text-2xl font-bold text-white text-center mb-8" data-id="element-64">
            El Mecanismo: 3 Piezas que Encajan
          </h3>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-65">
            {[{
            icon: Zap,
            title: '1. Oferta-Minuto',
            desc: "Elegís un micro‑servicio simple y lo empaquetás en 60 minutos. Sin vueltas. Sin 'marca personal'. Sin esperar sentirte listo."
          }, {
            icon: ShieldCheck,
            title: '2. Portafolio Express',
            desc: 'Armás prueba real (antes/después o demo) en 48hs sin inventar clientes. La forma honesta de mostrar que sabés hacerlo.'
          }, {
            icon: DollarSign,
            title: '3. Ruta de Cobro',
            desc: 'Guiones para abrir conversación, calificar, proponer, manejar objeciones y cobrar sin incomodidad.'
          }].map((feature, i) => <div key={i} className="relative group" data-id="element-66">
                <div className="absolute inset-0 bg-blue-600/5 rounded-2xl transform group-hover:scale-105 transition-transform duration-300" data-id="element-67" />
                <div className="relative p-6 text-center" data-id="element-68">
                  <div className="w-16 h-16 mx-auto bg-zinc-900 rounded-full flex items-center justify-center border border-zinc-700 mb-4 group-hover:border-blue-500 transition-colors" data-id="element-69">
                    <feature.icon className="h-8 w-8 text-blue-500" data-id="element-70" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-3" data-id="element-71">
                    {feature.title}
                  </h4>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-72">
                    {feature.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </Section>

      {/* What's Included */}
      <Section data-id="element-73">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 md:p-12" data-id="element-74">
          <h2 className="text-3xl font-bold text-white mb-8 text-center" data-id="element-75">
            Qué incluye (Claro y Concreto)
          </h2>
          <div className="grid md:grid-cols-2 gap-y-4 gap-x-8" data-id="element-76">
            {['Lista de micro‑servicios para primera venta (entrega en 72h)', "Plantilla 'Oferta‑Minuto' (tu oferta se entiende en 10s)", 'Cómo armar un portafolio honesto en 48h', 'Cómo dejar tu perfil listo para que te consulten', 'Cómo armar tu lista de 100 prospectos', 'Scripts DM (copiar/pegar) para abrir, proponer, cerrar', "Respuestas a objeciones típicas ('no tengo plata', 'lo pienso')", 'Plan día por día (21 días) + tablero de seguimiento'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-77">
                <Check className="h-5 w-5 text-blue-500 mt-1 shrink-0" data-id="element-78" />
                <span className="text-gray-300" data-id="element-79">{item}</span>
              </div>)}
          </div>
        </div>
      </Section>

      {/* Testimonials */}
      <Section data-id="element-80">
        <div className="text-center mb-12" data-id="element-81">
          <span className="text-blue-500 font-bold tracking-wider uppercase text-sm" data-id="element-82">
            Prueba real
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mt-2" data-id="element-83">
            Pibes que ya hicieron su primera venta
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4" data-id="element-84">
          {[{
          name: 'Tomás R.',
          age: 21,
          city: 'Córdoba',
          service: 'Edición de Reels',
          text: 'Día 9 cerré mi primer cliente. Un local de ropa que necesitaba reels. Le cobré $15.000 por 4 videos. Nunca pensé que iba a ser tan directo. Los scripts de DM me salvaron la vida.'
        }, {
          name: 'Camila S.',
          age: 19,
          city: 'Rosario',
          service: 'Diseño de logos',
          text: 'Tenía vergüenza de ofrecer algo porque "no soy diseñadora profesional". Armé el portafolio con 3 demos en una tarde y al segundo día ya tenía una consulta. Cobré mi primer logo a $8.000.'
        }, {
          name: 'Nico M.',
          age: 23,
          city: 'CABA',
          service: 'Community Management',
          text: 'Llevaba meses viendo videos de marketing sin hacer nada. Con la Oferta-Minuto armé un paquete de manejo de Instagram en 1 hora. En la semana 2 ya tenía 2 clientes fijos.'
        }, {
          name: 'Valentina G.',
          age: 20,
          city: 'Mendoza',
          service: 'Transcripciones',
          text: 'Elegí algo re simple: transcribir audios y videos. No necesité saber nada técnico. El día 12 cobré mi primera transcripción. $5.000 por 2 horas de laburo. Ahora hago 3 por semana.'
        }, {
          name: 'Facu L.',
          age: 22,
          city: 'La Plata',
          service: 'Armado de CVs',
          text: 'Me parecía una boludez vender CVs. Pero seguí el plan, armé 2 demos, mandé 30 mensajes con el script y cerré 4 clientes en 10 días. Ya llevo más de 20 CVs hechos.'
        }, {
          name: 'Martina P.',
          age: 18,
          city: 'Tucumán',
          service: 'Subtítulos para videos',
          text: 'Tengo 18 y nunca había ganado plata por mi cuenta. Empecé con subtítulos para creadores de contenido. El guion de objeciones me re sirvió cuando me dijeron "lo pienso". Hoy tengo 3 clientes.'
        }, {
          name: 'Lautaro D.',
          age: 24,
          city: 'Mar del Plata',
          service: 'Flyers y posts',
          text: 'Usaba Canva de hobby. Nunca se me ocurrió cobrar por eso. Con la plantilla de oferta armé un servicio de "pack de 10 posts" y en la semana 1 ya tenía un cliente. Fue un antes y después.'
        }, {
          name: 'Sofía A.',
          age: 21,
          city: 'Salta',
          service: 'Respuestas a comentarios',
          text: 'Mi servicio es re nicho: gestionar comentarios y DMs de cuentas de Instagram. Nadie lo ofrece y todos lo necesitan. Día 7 cerré mi primer cliente. El plan día por día no te deja procrastinar.'
        }, {
          name: 'Mateo V.',
          age: 23,
          city: 'Neuquén',
          service: 'Edición de podcasts',
          text: 'Sabía editar audio básico y nada más. Armé la oferta, hice una demo gratis para un podcaster chico y me contrató al toque. Ahora le edito 4 episodios por mes. Paso de no llegar a fin de mes a tener un ingreso fijo.'
        }].map((testimonial, i) => <div key={i} className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5 flex flex-col" data-id="element-85">
              <Quote className="h-5 w-5 text-blue-500/40 mb-3 shrink-0" data-id="element-86" />
              <p className="text-gray-300 text-sm leading-relaxed flex-1 mb-4" data-id="element-87">
                "{testimonial.text}"
              </p>
              <div className="flex items-center justify-between border-t border-zinc-800 pt-3 mt-auto" data-id="element-88">
                <div data-id="element-89">
                  <p className="text-white font-semibold text-sm" data-id="element-90">
                    {testimonial.name}
                  </p>
                  <p className="text-zinc-500 text-xs" data-id="element-91">
                    {testimonial.age} años · {testimonial.city}
                  </p>
                </div>
                <span className="text-xs text-blue-400 bg-blue-900/20 px-2 py-1 rounded-full border border-blue-800/30" data-id="element-92">
                  {testimonial.service}
                </span>
              </div>
              <div className="flex gap-0.5 mt-2" data-id="element-93">
                {[...Array(5)].map((_, s) => <Star key={s} className="h-3 w-3 fill-yellow-500 text-yellow-500" data-id="element-94" />)}
              </div>
            </div>)}
        </div>
      </Section>

      {/* Reality Check */}
      <Section className="bg-black" data-id="element-95">
        <div className="max-w-2xl mx-auto text-center" data-id="element-96">
          <Lock className="h-12 w-12 text-zinc-600 mx-auto mb-6" data-id="element-97" />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6" data-id="element-98">
            La parte incómoda (pero real)
          </h2>
          <p className="text-gray-400 mb-6" data-id="element-99">
            Vas a sentir vergüenza al principio. Porque pasar de mirar contenido
            a ofrecer algo real te pone en riesgo: riesgo de que te ignoren, de
            que te digan que no.
          </p>
          <p className="text-white font-medium text-lg" data-id="element-100">
            Pero esa incomodidad dura poco. <br data-id="element-101" />
            Lo que dura meses (o años) es seguir consumiendo ideas y llegando
            tarde a fin de mes.
          </p>
        </div>
      </Section>

      {/* Offer / CTA */}
      <Section id="offer" className="py-20" data-id="element-102">
        <Card glow className="max-w-lg mx-auto text-center border-blue-500/30 bg-gradient-to-b from-zinc-900 to-black" data-id="element-103">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-bold tracking-wide shadow-lg shadow-blue-900/50" data-id="element-104">
            OFERTA LIMITADA
          </div>

          <h2 className="text-3xl font-black text-white mt-6 mb-2" data-id="element-105">
            PLAN PRIMER DÓLAR
          </h2>
          <p className="text-zinc-400 mb-8" data-id="element-106">
            El manual de ejecución para tu primera venta.
          </p>

          <div className="flex items-center justify-center gap-2 mb-8" data-id="element-107">
            <span className="text-zinc-500 text-2xl line-through" data-id="element-108">
              $27.000 ARS
            </span>
            <span className="text-5xl font-black text-white" data-id="element-109">$8.990 ARS</span>
          </div>

          <ul className="text-left space-y-3 mb-8 max-w-xs mx-auto" data-id="element-110">
            <li className="flex items-center text-sm text-gray-300" data-id="element-111">
              <Check className="h-4 w-4 text-blue-500 mr-2" data-id="element-112" /> Acceso inmediato
              al PDF
            </li>
            <li className="flex items-center text-sm text-gray-300" data-id="element-113">
              <Check className="h-4 w-4 text-blue-500 mr-2" data-id="element-114" /> Plantillas y
              Scripts incluidos
            </li>
            <li className="flex items-center text-sm text-gray-300" data-id="element-115">
              <Check className="h-4 w-4 text-blue-500 mr-2" data-id="element-116" /> Actualizaciones
              futuras gratis
            </li>
          </ul>

          <Button fullWidth size="lg" className="text-lg mb-4 animate-pulse hover:animate-none" onClick={() => {
          trackEvent('AddToCart', {
            content_name: 'Plan Primer Dólar',
            content_type: 'product',
            value: 8990,
            currency: 'ARS'
          });
          window.open('https://fondoinicial.online/cart/47689212428527:1', '_blank');
        }} data-id="element-117">
            DESCARGAR AHORA
          </Button>

          <p className="text-xs text-zinc-500" data-id="element-118">
            Pago seguro. Garantía de satisfacción de 7 días.
          </p>
        </Card>
      </Section>

      {/* Legal Modal */}
      <AnimatePresence data-id="element-119">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-120">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25
        }} className="relative w-full max-w-lg max-h-[80vh] overflow-y-auto bg-zinc-900 border border-zinc-700 rounded-2xl p-6 md:p-8 shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-121">
              <button onClick={() => setActiveModal(null)} className="absolute top-4 right-4 text-zinc-400 hover:text-white transition-colors" aria-label="Cerrar" data-id="element-122">
                <X className="h-5 w-5" data-id="element-123" />
              </button>

              {activeModal === 'privacy' && <div data-id="element-124">
                  <h3 className="text-2xl font-bold text-white mb-6" data-id="element-125">
                    Política de Privacidad
                  </h3>
                  <div className="space-y-4 text-sm text-gray-400 leading-relaxed" data-id="element-126">
                    <p data-id="element-127">
                      <span className="text-gray-200 font-semibold" data-id="element-128">
                        Última actualización:
                      </span>{' '}
                      Febrero 2026
                    </p>
                    <p data-id="element-129">
                      En Plan Primer Dólar, respetamos tu privacidad. Esta
                      política describe cómo recopilamos, usamos y protegemos tu
                      información personal.
                    </p>
                    <h4 className="text-white font-semibold text-base pt-2" data-id="element-130">
                      Información que recopilamos
                    </h4>
                    <p data-id="element-131">
                      Recopilamos únicamente la información necesaria para
                      procesar tu compra: nombre, dirección de correo
                      electrónico y datos de pago. No almacenamos datos de
                      tarjetas de crédito directamente; estos son procesados por
                      proveedores de pago seguros.
                    </p>
                    <h4 className="text-white font-semibold text-base pt-2" data-id="element-132">
                      Uso de la información
                    </h4>
                    <p data-id="element-133">
                      Tu información se utiliza exclusivamente para: entregar el
                      producto digital adquirido, enviar actualizaciones
                      relacionadas con tu compra y brindarte soporte si lo
                      necesitás.
                    </p>
                    <h4 className="text-white font-semibold text-base pt-2" data-id="element-134">
                      Protección de datos
                    </h4>
                    <p data-id="element-135">
                      Implementamos medidas de seguridad estándar de la
                      industria para proteger tu información personal contra
                      acceso no autorizado, alteración o destrucción.
                    </p>
                    <h4 className="text-white font-semibold text-base pt-2" data-id="element-136">
                      Tus derechos
                    </h4>
                    <p data-id="element-137">
                      Podés solicitar acceso, corrección o eliminación de tus
                      datos personales en cualquier momento escribiéndonos a
                      nuestro correo de contacto.
                    </p>
                  </div>
                </div>}

              {activeModal === 'terms' && <div data-id="element-138">
                  <h3 className="text-2xl font-bold text-white mb-6" data-id="element-139">
                    Términos y Condiciones
                  </h3>
                  <div className="space-y-4 text-sm text-gray-400 leading-relaxed" data-id="element-140">
                    <p data-id="element-141">
                      <span className="text-gray-200 font-semibold" data-id="element-142">
                        Última actualización:
                      </span>{' '}
                      Febrero 2026
                    </p>
                    <p data-id="element-143">
                      Al adquirir Plan Primer Dólar (21 días), aceptás los
                      siguientes términos y condiciones.
                    </p>
                    <h4 className="text-white font-semibold text-base pt-2" data-id="element-144">
                      Producto digital
                    </h4>
                    <p data-id="element-145">
                      Plan Primer Dólar es un producto digital (PDF con
                      plantillas y guiones). Una vez realizada la compra,
                      recibirás acceso inmediato al material. Este producto es
                      para uso personal y no puede ser redistribuido, revendido
                      ni compartido.
                    </p>
                    <h4 className="text-white font-semibold text-base pt-2" data-id="element-146">
                      Garantía de satisfacción
                    </h4>
                    <p data-id="element-147">
                      Ofrecemos una garantía de satisfacción de 7 días. Si no
                      estás conforme con el producto, podés solicitar un
                      reembolso completo dentro de los 7 días posteriores a la
                      compra.
                    </p>
                    <h4 className="text-white font-semibold text-base pt-2" data-id="element-148">
                      Resultados
                    </h4>
                    <p data-id="element-149">
                      Los resultados dependen del esfuerzo y dedicación de cada
                      persona. No garantizamos ingresos específicos. El material
                      proporciona herramientas y guías, pero la ejecución
                      depende de vos.
                    </p>
                    <h4 className="text-white font-semibold text-base pt-2" data-id="element-150">
                      Propiedad intelectual
                    </h4>
                    <p data-id="element-151">
                      Todo el contenido del plan está protegido por derechos de
                      autor. Queda prohibida su reproducción total o parcial sin
                      autorización expresa.
                    </p>
                  </div>
                </div>}

              {activeModal === 'contact' && <div data-id="element-152">
                  <h3 className="text-2xl font-bold text-white mb-6" data-id="element-153">
                    Contacto
                  </h3>
                  <div className="space-y-6 text-sm text-gray-400" data-id="element-154">
                    <p className="leading-relaxed" data-id="element-155">
                      ¿Tenés dudas sobre el plan, problemas con tu compra o
                      simplemente querés hablar? Escribinos por cualquiera de
                      estos canales:
                    </p>
                    <div className="space-y-4" data-id="element-156">
                      <div className="flex items-center gap-3 bg-zinc-800/50 rounded-xl p-4 border border-zinc-700/50" data-id="element-157">
                        <Mail className="h-5 w-5 text-blue-500 shrink-0" data-id="element-158" />
                        <div data-id="element-159">
                          <p className="text-gray-300 font-medium" data-id="element-160">Email</p>
                          <p className="text-blue-400" data-id="element-161">
                            soporte@primerdolar.com
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 bg-zinc-800/50 rounded-xl p-4 border border-zinc-700/50" data-id="element-162">
                        <MessageCircle className="h-5 w-5 text-green-500 shrink-0" data-id="element-163" />
                        <div data-id="element-164">
                          <p className="text-gray-300 font-medium" data-id="element-165">
                            Instagram DM
                          </p>
                          <p className="text-blue-400" data-id="element-166">@primerdolar</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-500 text-xs" data-id="element-167">
                      Respondemos en menos de 24 horas.
                    </p>
                  </div>
                </div>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Footer */}
      <footer className="py-12 border-t border-zinc-900 text-center text-zinc-600 text-sm" data-id="element-168">
        <div className="max-w-4xl mx-auto px-4" data-id="element-169">
          <p className="mb-6" data-id="element-170">
            "No necesitás que te motive alguien. Necesitás una semana donde
            hagas lo que la mayoría no hace."
          </p>
          <div className="flex items-center justify-center gap-6 mb-6" data-id="element-171">
            <button onClick={() => setActiveModal('privacy')} className="text-zinc-500 hover:text-blue-400 transition-colors underline underline-offset-2" data-id="element-172">
              Privacidad
            </button>
            <span className="text-zinc-700" data-id="element-173">·</span>
            <button onClick={() => setActiveModal('terms')} className="text-zinc-500 hover:text-blue-400 transition-colors underline underline-offset-2" data-id="element-174">
              Términos
            </button>
            <span className="text-zinc-700" data-id="element-175">·</span>
            <button onClick={() => setActiveModal('contact')} className="text-zinc-500 hover:text-blue-400 transition-colors underline underline-offset-2" data-id="element-176">
              Contacto
            </button>
          </div>
          <p data-id="element-177">
            &copy; {new Date().getFullYear()} Plan Primer Dólar. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>
    </div>;
}